import { Box, Typography } from "@mui/material";
import { NOTE_KEY_PREFIX, setUnsavedWork } from "../features/appState/uxSlice";
import React, {
  ChangeEvent,
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useState,
} from "react";
import { createStyleSheet, globalStyles } from "../utils/styles";

import ClinicianBadge from "./ClinicianBadge";
import IconButton from "./IconButton";
import NoteAddIcon from "@mui/icons-material/Send";
import { NoteEntry } from "../features/data/patientApiSlice";
import TextInput from "./TextInput";
import { formatDateTime } from "../utils/dates";
import { useAppDispatch } from "../features/store";
import { useTranslation } from "react-i18next";

export type NotesRef = {
  getNoteValue: () => string;
};

type NotesProps = {
  notes: NoteEntry[];
  onAddNote?: (note: string) => void;
};

const Notes = forwardRef<NotesRef, NotesProps>((props, ref) => {
  const { t } = useTranslation();
  const { onAddNote } = props;
  const dispatch = useAppDispatch();
  const noteId = useId();

  // Component state
  const [addNote, setAddNote] = useState("");
  const hasUnsavedNote = !!onAddNote && addNote.trim() !== "";

  useImperativeHandle(ref, () => ({
    getNoteValue: () => addNote,
  }));

  // Callbacks
  const handleAddNote = useCallback(() => {
    const addNoteCandidate = addNote.trim();
    if (addNoteCandidate.length > 0) {
      onAddNote?.(addNoteCandidate);
      setAddNote("");
    }
  }, [addNote, onAddNote, setAddNote]);
  const handleAddNoteChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setAddNote(event.target.value);
    },
    [setAddNote]
  );
  const handleOnKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        handleAddNote();
        e.preventDefault();
      }
    },
    [handleAddNote]
  );

  // Reactive code
  useEffect(() => {
    if (!hasUnsavedNote) {
      return;
    }
    const key = `${NOTE_KEY_PREFIX}${noteId}`;
    dispatch(setUnsavedWork({ key, isDirty: true }));
    return () => {
      dispatch(setUnsavedWork({ key, isDirty: false }));
    };
  }, [dispatch, noteId, hasUnsavedNote]);

  return props.notes.length > 0 || !!props.onAddNote ? (
    <Box sx={styles.container}>
      {props.notes.map((note) => (
        <Box key={note.uid} sx={styles.noteRow}>
          <ClinicianBadge clinician={note.author} />
          <Box sx={styles.noteColumn}>
            <Box sx={styles.noteTitleRow}>
              <Typography variant="notesTitle">{note.author.name}</Typography>
              <Typography variant="notesSubtitle" sx={styles.noteSubtitleGap}>
                {formatDateTime(new Date(note.created))}
              </Typography>
            </Box>
            <Typography key={note.uid} variant="notesBody">
              {note.content}
            </Typography>
          </Box>
        </Box>
      ))}
      {props.onAddNote ? (
        <TextInput
          ariaLabel={"notes"}
          sx={styles.notesInput}
          inputProps={{
            placeholder: t("common:addNote"),
            onChange: handleAddNoteChange,
            value: addNote,
            onKeyDown: handleOnKeyDown,
            multiline: true,
            endAdornment: (
              <IconButton
                disabled={addNote.length === 0}
                icon={
                  <NoteAddIcon
                    fontSize="small"
                    color={addNote.length > 0 ? "primary" : "disabled"}
                  />
                }
                tooltip={t("common:addNote")}
                onClick={handleAddNote}
              />
            ),
          }}
        />
      ) : null}
    </Box>
  ) : null;
});

const styles = createStyleSheet({
  container: {
    ...globalStyles.gapTop,
    flex: 1,
    display: "flex",
    flexDirection: "column",
  },
  noteRow: {
    display: "flex",
    flexDirection: "row",
    marginBottom: 2,
  },
  noteColumn: {
    marginLeft: 1,
    display: "flex",
    flexDirection: "column",
  },
  noteTitleRow: {
    display: "flex",
    flexDirection: "row",
    alignItems: "baseline",
  },
  noteSubtitleGap: {
    marginLeft: 1,
  },
  notesInput: {
    border: (theme) => `1px solid ${theme.palette.text.disabled}`,
    "&:hover": {
      border: (theme) => `1px solid ${theme.palette.text.primary}`,
    },
    borderRadius: 1,
    paddingLeft: 1.5,
    paddingRight: 0.5,
  },
});

export default Notes;
